import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { tap, map, catchError } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';
import { AUTH_API_BASE } from './services/api-tokens';

interface LoginResponse {
  token?: string;
  message?: string;
}

interface ResetResponse {
  message?: string;
  success?: boolean;
}

interface JwtPayload {
  exp: number;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private authBase = inject(AUTH_API_BASE);
  private readonly TOKEN_KEY = 'jwtToken';

  // ✅ TEMP: hard-wired customer context
  private customerIdSubject = new BehaviorSubject<number | null>(null);
  customerId$ = this.customerIdSubject.asObservable();

  constructor() {
    const token = this.getToken();
    if (token && this.isTokenValid(token)) {
      this.customerIdSubject.next(30); // ✅ hard-wired for now
    }
  }

  // ==========================
  // LOGIN
  // ==========================
  login(username: string, password: string): Observable<boolean> {
    const url = `${this.authBase}/login`;

    // ✅ safest payload for your backend DTO
    const body = { username: username.trim(), password };

    console.log('[AuthService] Login URL:', url);

    return this.http.post<LoginResponse>(url, body).pipe(
      tap(res => {
        if (res.token) {
          localStorage.setItem(this.TOKEN_KEY, res.token);
          this.customerIdSubject.next(30); // TEMP
        }
      }),
      map(res => !!res.token),
      catchError(err => {
        console.error('[AuthService] Login error', err);
        return of(false);
      })
    );
  }

  // ==========================
  // PASSWORD RESET (✅ now used)
  // ==========================
  forgotPassword(email: string): Observable<boolean> {
    const url = `${this.authBase}/forgot-password`;
    const body = { email, app: 'Customer-app' };

    return this.http.post(url, body, { responseType: 'text' }).pipe(
      map(() => true),
      catchError(err => {
        console.error('[AuthService] Forgot password error', err);
        return of(false);
      })
    );
  }

  // ==========================
  // LOGOUT / SESSION
  // ==========================
  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    this.customerIdSubject.next(null);
    this.router.navigate(['/login']);
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    return token ? this.isTokenValid(token) : false;
  }

  private isTokenValid(token: string): boolean {
    try {
      const payload: JwtPayload = jwtDecode(token);
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp < now) {
        this.logout();
        return false;
      }
      return true;
    } catch {
      this.logout();
      return false;
    }
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getCurrentCustomerId(): number | null {
    return this.customerIdSubject.value;
  }
}
